import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { LoadStatus } from 'config/utils';
import { changeCustomModalOpen } from 'components/utils/dialog.helper';
import { ISubmitStripeBillingPayload } from '../types/stripe.types';
import { addStripePaymentMethod, createIntentAndCustomerSession, createStripeIntent } from '../services/stripe.api';

interface StripeState {
   stripeLoading: LoadStatus;
   addStripeBillingStatus: LoadStatus;
   createStripeIntentStatus: LoadStatus;
   createIntentAndCustomerSessionStatus: LoadStatus;
   customerId: string | undefined;
   customerClientSecret: string | undefined;
   customerSessionClientSecret: string | undefined;
   stripeFormModalOpen: boolean;
   stripeError: string | undefined;
}

const initialState: StripeState = {
   stripeLoading: LoadStatus.idle,
   addStripeBillingStatus: LoadStatus.idle,
   createStripeIntentStatus: LoadStatus.idle,
   createIntentAndCustomerSessionStatus: LoadStatus.idle,
   stripeFormModalOpen: false,
   customerId: '',
   customerClientSecret: '',
   customerSessionClientSecret: '',
   stripeError: '',
};

export const addStripePaymentMethodAsync = createAsyncThunk(
   'stripe/addBilling',
   async (billingInput: { bodyInput: ISubmitStripeBillingPayload }) =>
      await addStripePaymentMethod(billingInput.bodyInput),
);

export const createStripeIntentAsync = createAsyncThunk('stripe/createIntent', async () => await createStripeIntent());

export const createIntentAndCustomerSessionAsync = createAsyncThunk(
   'stripe/createIntentAndCustomerSession',
   async () => await createIntentAndCustomerSession(),
);

const stripeSlice = createSlice({
   name: 'stripe',
   initialState,
   reducers: {
      setStripeLoading: (state, action: PayloadAction<LoadStatus>) => {
         state.stripeLoading = action.payload;
      },
      setStripeFormModalOpen: (state, action: PayloadAction<boolean>) => {
         changeCustomModalOpen(action.payload);
         state.stripeFormModalOpen = action.payload;
      },
      setStripeError: (state, action: PayloadAction<string | undefined>) => {
         state.stripeError = action.payload;
      },
      resetAddStripeBillingStatus: (state) => {
         state.addStripeBillingStatus = LoadStatus.idle;
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(addStripePaymentMethodAsync.pending, (state) => {
            state.addStripeBillingStatus = LoadStatus.loading;
         })
         .addCase(addStripePaymentMethodAsync.fulfilled, (state) => {
            state.addStripeBillingStatus = LoadStatus.complete;
         })
         .addCase(addStripePaymentMethodAsync.rejected, (state) => {
            state.addStripeBillingStatus = LoadStatus.failed;
         })
         .addCase(createStripeIntentAsync.pending, (state) => {
            state.createStripeIntentStatus = LoadStatus.loading;
         })
         .addCase(createStripeIntentAsync.fulfilled, (state, action) => {
            state.createStripeIntentStatus = LoadStatus.complete;
            state.customerId = action.payload.customerId;
            state.customerClientSecret = action.payload.customerClientSecret;
         })
         .addCase(createStripeIntentAsync.rejected, (state) => {
            state.createStripeIntentStatus = LoadStatus.failed;
         })
         .addCase(createIntentAndCustomerSessionAsync.pending, (state) => {
            state.createIntentAndCustomerSessionStatus = LoadStatus.loading;
         })
         .addCase(createIntentAndCustomerSessionAsync.fulfilled, (state, action) => {
            state.createIntentAndCustomerSessionStatus = LoadStatus.complete;
            state.customerId = action.payload.customerId;
            state.customerSessionClientSecret = action.payload.customerSessionClientSecret;
         })
         .addCase(createIntentAndCustomerSessionAsync.rejected, (state) => {
            state.createIntentAndCustomerSessionStatus = LoadStatus.failed;
         });
   },
});

export const { setStripeFormModalOpen, resetAddStripeBillingStatus, setStripeLoading, setStripeError } =
   stripeSlice.actions;

export const stripeStore = (state: RootState) => state.stripeStore;

export default stripeSlice.reducer;
